import { SplitFactory } from '@splitsoftware/splitio'

export interface SplitManager {
  getActive: () => { client: SplitIO.IBrowserClient, ready: Promise<boolean> }
}

interface ActiveClient {
  userId: string
  client: SplitIO.IBrowserClient
  ready: Promise<boolean>
}

// Stable per session so anonymous users keep one treatment bucket instead of
// being re-randomised on every flag check.
let anonKey: string | null = null

let active: ActiveClient | null = null

function currentUserId (): string {
  const authUser = getLocal('auth_user')
  if (authUser) {
    try {
      const id = JSON.parse(authUser)?.id
      if (id) return String(id)
    } catch {
      // fall through to anon key on malformed storage
    }
  }
  if (!anonKey) {
    try {
      anonKey = crypto.randomUUID()
    } catch {
      const bytes = new Uint8Array(16)
      crypto.getRandomValues(bytes)
      const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
      anonKey = 'anon-' + hex
    }
  }
  return anonKey
}

function getActive (splitAuthKey: string): ActiveClient {
  const userId = currentUserId()
  if (active && active.userId === userId) {
    return active
  }

  // Identity changed (login/logout) — retire the old client so targeting
  // follows the current user, then boot one keyed to the new identity.
  void active?.client.destroy()

  const client = SplitFactory({
    core: {
      authorizationKey: splitAuthKey,
      key: userId,
      trafficType: 'user'
    }
  }).client() as SplitIO.IBrowserClient

  const ready = client.ready()
    .then(() => true)
    .catch(() => {
      // SDK timed out: report not-ready (flags resolve off) and clear the
      // cache so the next lookup retries with a fresh client.
      if (active && active.userId === userId) {
        active = null
      }
      void client.destroy()
      return false
    })

  active = { userId, client, ready }
  return active
}

export default defineNuxtPlugin(nuxtApp => {
  const { splitAuthKey } = useRuntimeConfig().public

  // Warm the SDK at app boot so split rules are already downloaded before the
  // first page-level flag check runs.
  getActive(splitAuthKey)

  const manager: SplitManager = {
    getActive: () => getActive(splitAuthKey)
  }

  nuxtApp.provide('split', manager)
})
